// ─── Champ ADRESSE réutilisable (recherche + Ma position + carte) ─────
// Saisie d'adresse avec autocomplétion (BAN + Photon), bouton GPS et aperçu
// carte OSM. Renvoie l'adresse ET les coordonnées via onChange(adresse, coords).
// Utilisé par Lieux et Contacts (feature-calendar).
import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Image, StyleSheet, Platform } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { stylesForm } from '../ui/stylesForm';
import { rechercherLieu, formatAdresseNominatim, nomPrincipalLieu, adresseCourte, typeIconLieu, adresseDepuisCoords } from './rechercheLieu';
import { demanderItineraire, lienCarteEmbed, lienCarteStatique, positionDisponible, positionActuelle } from './itineraire';

export default function ChampAdresse({ adresse = '', coords = null, onChange, placeholder }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [sugg, setSugg] = useState([]);
  const [cherche, setCherche] = useState(false);
  const [gps, setGps] = useState(false);
  const rech = useRef(null);

  const onTexte = (v) => {
    onChange?.(v, null);   // saisie manuelle → plus de coordonnées tant qu'on ne choisit pas
    if (rech.current) clearTimeout(rech.current);
    const q = v.trim();
    if (q.length < 3) { setSugg([]); setCherche(false); return; }
    setCherche(true);
    rech.current = setTimeout(async () => { const r = await rechercherLieu(q); setSugg(r); setCherche(false); }, 350);
  };
  const choisir = (it) => {
    const lat = parseFloat(it.lat), lng = parseFloat(it.lon);
    onChange?.(formatAdresseNominatim(it), (!Number.isNaN(lat) && !Number.isNaN(lng)) ? { lat, lng } : null);
    setSugg([]); setCherche(false);
  };
  const maPosition = async () => {
    setGps(true);
    const p = await positionActuelle();
    if (p) { const adr = await adresseDepuisCoords(p.lat, p.lng); onChange?.(adr || adresse, p); setSugg([]); setCherche(false); }
    setGps(false);
  };
  const ouvrirItineraire = () => demanderItineraire({ ...(coords || {}), adresse });

  const carte = coords ? (() => {
    const embed = lienCarteEmbed(coords);
    let WV = null;
    if (Platform.OS !== 'web' && embed) { try { WV = require('react-native-webview').WebView; } catch { WV = null; } }
    if (WV) return <View style={s.carteWrap}><WV source={{ uri: embed }} style={s.carteImg} /></View>;
    const img = lienCarteStatique(coords);
    return img ? (
      <TouchableOpacity style={s.carteWrap} activeOpacity={0.85} onPress={ouvrirItineraire}>
        <Image source={{ uri: img }} style={s.carteImg} resizeMode="cover" />
      </TouchableOpacity>
    ) : null;
  })() : null;

  return (
    <View>
      <View style={s.row}>
        <TextInput style={[s.input, { flex: 1, minHeight: 50 }]} value={adresse} onChangeText={onTexte} multiline
          placeholder={placeholder || t('lieux.rechercheAdresse')} placeholderTextColor={UI.faint} />
        {positionDisponible() && (
          <TouchableOpacity style={s.posIcon} onPress={maPosition} disabled={gps} accessibilityLabel={t('lieux.maPosition')}>
            <Text style={{ fontSize: 20, opacity: gps ? 0.4 : 1 }}>📍</Text>
          </TouchableOpacity>
        )}
      </View>
      {cherche && <Text style={s.hint}>{t('lieux.recherche')}</Text>}
      {sugg.length > 0 && (
        <View style={s.sugg}>
          {sugg.map((it, i) => (
            <TouchableOpacity key={(it.place_id || '') + i} style={s.suggItem} activeOpacity={0.7} onPress={() => choisir(it)}>
              <Text style={{ fontSize: 16 }}>{typeIconLieu(it)}</Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={s.suggNom} numberOfLines={1}>{nomPrincipalLieu(it)}</Text>
                <Text style={s.suggSous} numberOfLines={1}>{adresseCourte(it) || it.display_name}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}
      {carte}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  posIcon: { width: 42, height: 42, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  hint: { fontSize: 12, color: UI.faint, marginTop: 6 },
  sugg: { marginTop: 6, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, overflow: 'hidden' },
  suggItem: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 11, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  suggNom: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  suggSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  carteWrap: { marginTop: 10, borderRadius: 12, overflow: 'hidden', borderWidth: 0.5, borderColor: UI.border },
  carteImg: { width: '100%', height: 150, backgroundColor: UI.card },
});
